/** @format */

import React, { useEffect, useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Box } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Page from 'src/components/Page';
import { Button, WidgetGrid, WelcomeTitle } from 'src/components';
import { useTranslation } from 'react-i18next';
import Breadcrumb from 'src/components/Breadcrumb';
import { useDispatch, useSelector } from 'src/store';
import { useHistory, Redirect } from 'react-router-dom';
import { getDashboard, getWidgetInfo, setCurrentUserDashboard } from 'src/slices/users';
import useAuth from 'src/hooks/useAuth';
import { HELP_DASHBOARD_ID } from 'src/constants';
import OnBoardingView from './OnBoardingView';

const startViewTransition = callback => {
  if (document.startViewTransition) {
    document.startViewTransition(() => {
      flushSync(callback);
    });
  } else {
    callback();
  }
};

function OnboardingPageView() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { dashboardLoading, dashboardError, dashboardInfo, userLoading, widgetInfo } = useSelector(
    state => state.users
  );

  const isHelpDashboard = !!user?.onBoardingComplete;

  const [mainReady, setMainReady] = useState(false);
  const [helpReady, setHelpReady] = useState(false);
  const prevDashboardLoadingRef = useRef(dashboardLoading);
  const prevUserLoadingRef = useRef(userLoading);
  const [modalOpen, setModalOpen] = useState(false);

  const contentReady = isHelpDashboard ? helpReady : mainReady;

  useEffect(() => {
    if (contentReady) setModalOpen(true);
  }, [contentReady]);

  useEffect(() => {
    if (isHelpDashboard) {
      dispatch(getDashboard({ dashboardId: String(HELP_DASHBOARD_ID) }));
      dispatch(setCurrentUserDashboard(HELP_DASHBOARD_ID));
    } else {
      dispatch(getWidgetInfo());
      dispatch(setCurrentUserDashboard(null));
    }
  }, [dispatch, isHelpDashboard]);

  useEffect(() => {
    if (isHelpDashboard) {
      const wasLoading = prevDashboardLoadingRef.current;
      prevDashboardLoadingRef.current = dashboardLoading;
      if (wasLoading && !dashboardLoading) {
        startViewTransition(() => setHelpReady(true));
      } else if (!dashboardLoading) {
        setHelpReady(true);
      } else {
        setHelpReady(false);
      }
    } else {
      const loading = user && (userLoading || widgetInfo === null);
      const wasLoading = prevUserLoadingRef.current;
      prevUserLoadingRef.current = loading;
      if (wasLoading && !loading) {
        startViewTransition(() => setMainReady(true));
      } else if (!loading) {
        setMainReady(true);
      } else {
        setMainReady(false);
      }
    }
  }, [isHelpDashboard, dashboardLoading, dashboardInfo, userLoading, widgetInfo, user]);

  const handleBackToMainDashboard = () => {
    dispatch(setCurrentUserDashboard(null));
    history.push('/dashboard');
  };

  const handleCloseModal = () => setModalOpen(false);

  if (isHelpDashboard && dashboardError) return <Redirect to="/404" />;

  const welcomeTitle = `${user?.firstName && t('welcome')} ${user?.firstName} ${user?.lastName} ${user?.firstName ? '!' : ''}`;

  return (
    <Page className="dashboard" title="Dashboard">
      <Breadcrumb data={[{ title: t('dashboard') }]} />
      {isHelpDashboard && (
        <Box className="container">
          <Button borderOnly back onClick={handleBackToMainDashboard}>
            Main dashboard
          </Button>
        </Box>
      )}
      {!isHelpDashboard && <WelcomeTitle>{welcomeTitle}</WelcomeTitle>}
      {/* <Box style={{ viewTransitionName: 'dashboard-grid' }}> */}
      <Box>
        {isHelpDashboard ? helpReady && <WidgetGrid dashboardInfo={dashboardInfo} /> : mainReady && <WidgetGrid />}
      </Box>

      <Dialog
        fullWidth
        maxWidth="md"
        open={modalOpen}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleCloseModal();
        }}
        PaperProps={{
          style: { borderRadius: 10, maxWidth: 1110 }
        }}
        aria-labelledby="onboarding-dialog"
      >
        <DialogContent style={{ padding: 0, overflow: 'hidden' }}>
          <OnBoardingView isModal onCloseModal={handleCloseModal} />
        </DialogContent>
      </Dialog>
    </Page>
  );
}

export default OnboardingPageView;
